// Включено ли в опросе e2e шифрование в опросе
export let isE2EEnabled = false;

// Публичный RSA-OAEP ключ, максимальная длина кодируемого текста 255 символов
export let publicCryptoKey: CryptoKey | null = null;

// Секретный ключ AES, нужен для кодирования текста произвольной длины, оборачивается с помощью Публичный RSA-OAEP ключа
export let secretCryptoKey: CryptoKey | null = null;

export function setE2EEnabled(isEnabled: boolean) {
  isE2EEnabled = isEnabled;
}

export function setPublicCryptoKey(cryptoKey: CryptoKey) {
  publicCryptoKey = cryptoKey;
}

export function setSecretCryptoKey(cryptoKey: CryptoKey) {
  secretCryptoKey = cryptoKey;
}

/** Генерируем секретный ключ для зашифровки сообщений(ввода пользователя) */
export async function generateSecretKey() {
  return crypto.subtle.generateKey({ name: 'AES-CBC', length: 256 }, true, ['encrypt']);
}

export function importRsaKey(pem: string) {
  try {
    // fetch the part of the PEM string between header and footer
    const pemHeader = '-----BEGIN PUBLIC KEY-----';
    const pemFooter = '-----END PUBLIC KEY-----';
    const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length - 1);
    // base64 decode the string to get the binary data
    const binaryDerString = window.atob(pemContents);
    // convert from a binary string to an ArrayBuffer
    const binaryDer = str2ab(binaryDerString);

    return window.crypto.subtle.importKey('spki', binaryDer, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, [
      'encrypt',
      'wrapKey',
    ]);
  } catch (e) {
    return Promise.reject(e);
  }
}

/** Возвращает вектор инициализации для AES-CBC шифра */
function generateInitialVector() {
  // Вектор должен быть уникальным для каждой операции шифрования, выполняемой с данным ключом
  return window.crypto.getRandomValues(new Uint8Array(16));
}

/** Оборачиваем секретный ключ с помощью публичного */
async function wrapSecretKey(secretKey: CryptoKey, wrappingKey: CryptoKey) {
  return window.crypto.subtle.wrapKey('raw', secretKey, wrappingKey, 'RSA-OAEP');
}

/** Шифрует сообщение и возвращает результат в base64 */
export async function encrypt(text: string, { cause }: { cause: 'e2e' | 'pp' }): Promise<string> {
  try {
    if (!publicCryptoKey) return text;

    /** Если отсутствует секретный ключ(чего быть не должно) используем шифрование на основе публичного ключа */
    if (!secretCryptoKey) {
      const ab = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicCryptoKey, text2ab(text));
      return ab2base64(ab);
    }

    const iv = generateInitialVector();
    const encryptedText = await window.crypto.subtle.encrypt({ name: 'AES-CBC', iv }, secretCryptoKey, text2ab(text));
    const wrappedKey = await wrapSecretKey(secretCryptoKey, publicCryptoKey);

    return JSON.stringify({ key: ab2base64(wrappedKey), text: ab2base64(encryptedText), iv: ab2base64(iv), cause });
  } catch (error) {
    console.error('Failed to encrypt text: ', error);
    return text;
  }
}

function ab2base64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0, len = bytes.byteLength; i < len; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return window.btoa(binary);
}

function str2ab(str: string) {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

function text2ab(text: string) {
  const enc = new TextEncoder();
  return enc.encode(text);
}
